import { useAuthActions } from "@convex-dev/auth/react";
import { useConvexAuth } from "convex/react";
import { useState } from "react";
import { Navigate } from "react-router";
import { Button, Card, Input, Label, PasswordInput } from "@repo/ui";

interface LoginPageProps {
  title: string;
}

/** Même seuil que la validation côté serveur du provider Password. */
const LONGUEUR_MDP_MIN = 8;

/**
 * Message unique, que l'adresse ait un compte ou non.
 *
 * Distinguer les deux cas transformerait ce formulaire en oracle : n'importe
 * qui pourrait tester des adresses pour savoir lesquelles ont un compte chez un
 * client. On annonce donc toujours la même chose.
 */
const MSG_CODE_ENVOYE =
  "Si un compte existe pour cette adresse, un code vient d'être envoyé. Il expire dans 15 minutes.";

export function LoginPage({ title }: LoginPageProps) {
  const { signIn } = useAuthActions();
  const { isAuthenticated } = useConvexAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // `oubli` : l'écran bascule entre connexion et réinitialisation. La
  // vérification du code s'y fait aussi — une fois le nouveau mot de passe
  // accepté, Convex Auth renvoie des jetons et l'utilisateur est connecté dans
  // la foulée, sans repasser par le formulaire de connexion.
  const [oubli, setOubli] = useState(false);
  const [code, setCode] = useState("");
  const [nouveauMotDePasse, setNouveauMotDePasse] = useState("");
  const [infoReset, setInfoReset] = useState<string | null>(null);

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await signIn("password", { email, password, flow: "signIn" });
    } catch {
      setError("Email ou mot de passe incorrect.");
    } finally {
      setIsLoading(false);
    }
  };

  const demanderCode = async () => {
    setError(null);
    setInfoReset(null);
    setIsLoading(true);
    try {
      await signIn("password", { email, flow: "reset" });
    } catch {
      // Silencieux VOLONTAIREMENT : une adresse inconnue lève, et le dire
      // révélerait quels comptes existent. Même message dans les deux cas.
    } finally {
      setInfoReset(MSG_CODE_ENVOYE);
      setIsLoading(false);
    }
  };

  const validerCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfoReset(null);
    setIsLoading(true);
    try {
      // En cas de succès, la réponse porte des jetons : `isAuthenticated` passe
      // à vrai et le `Navigate` en tête de composant redirige vers l'accueil.
      await signIn("password", {
        email,
        code,
        newPassword: nouveauMotDePasse,
        flow: "reset-verification",
      });
    } catch {
      // Ne pas distinguer « code faux » de « code expiré ».
      setError("Code invalide ou expiré.");
    } finally {
      setIsLoading(false);
    }
  };

  const basculerVers = (versOubli: boolean) => {
    setOubli(versOubli);
    setError(null);
    setInfoReset(null);
    setCode("");
    setNouveauMotDePasse("");
    setPassword("");
  };

  return (
    <div className="flex min-h-screen w-screen items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-sm p-8">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {oubli
              ? "Réinitialisez votre mot de passe"
              : "Connectez-vous pour accéder au tableau de bord"}
          </p>
        </div>

        {oubli ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@exemple.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={isLoading || email === ""}
              onClick={() => void demanderCode()}
            >
              {isLoading ? "Envoi en cours…" : "Recevoir un code par email"}
            </Button>

            {infoReset && (
              <p className="text-sm text-muted-foreground">{infoReset}</p>
            )}

            {/* Champs TOUJOURS rendus, pas seulement après l'envoi : un code
                vaut 15 minutes, soit le temps d'aller le lire dans sa boîte.
                Les masquer derrière l'état « code demandé » les ferait
                disparaître au moindre rechargement, code valide en main. */}
            <form onSubmit={(e) => void validerCode(e)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code reçu par email</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  placeholder="12345678"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoComplete="one-time-code"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nouveau-mdp">Nouveau mot de passe</Label>
                <PasswordInput
                  id="nouveau-mdp"
                  value={nouveauMotDePasse}
                  onChange={(e) => setNouveauMotDePasse(e.target.value)}
                  autoComplete="new-password"
                />
                <p className="text-xs text-muted-foreground">
                  {LONGUEUR_MDP_MIN} caractères minimum.
                </p>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button
                type="submit"
                className="w-full"
                disabled={
                  isLoading ||
                  email === "" ||
                  code === "" ||
                  nouveauMotDePasse.length < LONGUEUR_MDP_MIN
                }
              >
                {isLoading
                  ? "Validation…"
                  : "Définir le mot de passe et se connecter"}
              </Button>
            </form>

            <button
              type="button"
              className="w-full text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={() => basculerVers(false)}
            >
              Retour à la connexion
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@exemple.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Connexion en cours..." : "Se connecter"}
            </Button>

            {/* `button` et non `a` : rien n'est navigué, on change d'écran dans
                la même page. Un lien sans href ne serait pas actionnable au
                clavier. */}
            <button
              type="button"
              className="w-full text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={() => basculerVers(true)}
            >
              Mot de passe oublié ?
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
